import dateUtil from "./dateUtil";

const getFileInfo = (file) => {
    const keys = [
        'lastModified',
        'lastModifiedDate',
        'name',
        'size',
        'type',
        'webkitRelativePath',
    ]
    return keys.reduce((acc, e) => ({...acc, [e]: file[e]}), {});
}

const difference = (obj1, obj2) => {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    return [
        ...keys2.filter((key2) => !keys1.includes(key2)),
        ...keys1.filter(key => {
            const val1 = obj1[key];
            const val2 = obj2[key];

            if(val1 instanceof Date && val2 instanceof Date){
                return !dateUtil.isSameDateAndTime(val1, val2);
            }

            if (isObject(val1) && isObject(val2)) {
                return !deepEqual(val1, val2);
            }

            if (isArray(val1) && isArray(val2)) {
                const sortedVal1 = val1.slice().sort();
                const sortedVal2 = val2.slice().sort();
                return !deepEqual(sortedVal1, sortedVal2);
            }

            return val1 !== val2;
        })
    ];
};

const deepEqual = (obj1, obj2) => {
    const keys1 = Object.keys(obj1 ?? {});
    const keys2 = Object.keys(obj2 ?? {});

    if (keys1.length !== keys2.length) {
        return false;
    }

    return keys1.every(key => {
        const val1 = obj1[key];
        const val2 = obj2[key];

        if(val1 instanceof Date && val2 instanceof Date){
            return dateUtil.isSameDateAndTime(val1, val2);
        }

        if (isObject(val1) && isObject(val2)) {
            return deepEqual(val1, val2);
        }

        if (isArray(val1) && isArray(val2)) {
            if (val1.length !== val2.length) {
                return false;
            }
            const sortedVal1 = val1.slice().sort();
            const sortedVal2 = val2.slice().sort();
            return sortedVal1.every((elem, index) => deepEqual(elem, sortedVal2[index]));
        }

        return val1 === val2;
    });
};

const stringifyQueryParams = (params, parentKey = null) => {
    const queryParts = [];

    const encodeParams = (key, value, index = null) => {
        return `${key}${index !== null ? `[${index}]`: ''}=${encodeURIComponent(value)}`;
    };

    for (const key in params) {
        if (params && params?.hasOwnProperty(key)) {
            const value = params[key];
            const encodedKey = parentKey ? `${parentKey}[${encodeURIComponent(key)}]` : encodeURIComponent(key);

            if (isArray(value)) {
                value.forEach((val, index) => {
                    if (isObject(val) || isArray(val)) {
                        queryParts.push(stringifyQueryParams(val, `${encodedKey}[${index}]`));
                    } else {
                        queryParts.push(encodeParams(encodedKey, val, index));
                    }
                });
            } else if (isObject(value)) {
                queryParts.push(stringifyQueryParams(value, encodedKey));
            } else {
                queryParts.push(encodeParams(encodedKey, value));
            }
        }
    }

    return queryParts.join('&');
};

const filterByKeys = (obj, keys, include = true) => Object.fromEntries(
    Object.entries(obj).filter(([key, _]) => keys.includes(key) === include)
);

const isArrayOrObject = (value) => value !== null && typeof value === 'object';
const isObject = (value) => isArrayOrObject(value) && !Array.isArray(value);
const isArray = (value) => isArrayOrObject(value) && Array.isArray(value);

/**
 * Return as tree from a flat array of objects with a parent key
 * @param array
 * @param key
 * @param parentKey
 * @param lastParent
 * @returns {*}
 */
const plainToTree = (array, key, parentKey, lastParent = null) => {
    /** New root are all obj with last parent as a direct parent */
    const root = lastParent === null
        ? array.filter((obj) => obj[parentKey] === null)
        : array.filter((obj) => lastParent === obj[parentKey]);
    const rootIds = root.map((obj) => obj[key]);
    const others = array.filter((obj) => !rootIds.includes(obj[key]))
    return root.map((obj) => ({
        ...filterByKeys(obj, [parentKey], false),
        children: plainToTree(others,key, parentKey, obj[key])
    }))
};

const treeToPlain = (array, childrenKey, identifier, parentKey, parent = null, depth = null) => {
    const result = [];
    const currentDepth = depth ?? 0;
    for(const elt of array){
        /** adding without children */
        result.push({...filterByKeys(elt, [childrenKey], false), [parentKey]: parent, depth: currentDepth});
        result.push(...treeToPlain(elt?.[childrenKey] ?? [], childrenKey, identifier, parentKey, elt?.[identifier], currentDepth + 1))
    }
    return result;
}

export default {
    deepEqual,
    isObject,
    isArray,
    toggleElement: (arr, val) => {
        if(isObject(val) || isArray(val)){
            return (arr.findIndex((el) => deepEqual(el, val)) !== -1) ? arr.filter(el => !deepEqual(el, val)) : [...arr, val];
        }
        return arr.includes(val) ? arr.filter(el => el !== val) : [...arr, val];
    },
    toQueryString: stringifyQueryParams,
    filterByKeys,
    swapObjects: (array, index1, index2) => {
        const temp = array[index1];
        array[index1] = array[index2];
        array[index2] = temp;
        return [...array];
    },
    deleteObjectByIndex: (arr, index) => {
        if (index < 0 || index >= arr.length) {
            return [...arr];
        }
        return [...(arr.slice(0, index).concat(arr.slice(index + 1)))];
    },
    getFileInfo,
    difference,
    treeToPlain,
    plainToTree
};

